import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import '../../../../library/elements/d-section.js';
import '../../../../library/elements/d-wrap.js';
import '../../../../library/editors/elements/d-checkbox.js';
import '../../../../library/components/d-popup.js';
import '../../../../library/editors/elements/d-edit-text.js';
import '../../../../library/editors/elements/d-select-dropdown.js';
import '../../../../library/editors/components/d-select-dropdown-or-add.js';
import {
  networkTypeOptions,
  responsiveFormStyles,
  getInternetConnectionTypeOptionsBySector,
} from 'src/pages/computers-page/infosec-procedure/defaults.js';
import type { Sector } from '../../../../store/types';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import type { NewNetworkResult } from './new-network-result';
import type { NewNetworkInput } from './new-network-input';
import type { DialogActionInput } from '../../../../library/components/DialogActionInput';

/**
 *
 * USAGE:
 *    d-infosec-computers-networks
 *
 */
@customElement('new-network-dialog')
export class NewNetworkDialog extends BaseDialog<NewNetworkInput, NewNetworkResult> {
  static readonly styles = [
    ...BaseDialog.styles,
    responsiveFormStyles,
    css`
      d-select-dropdown-or-add {
        min-width: 240px;
      }
    `,
  ];

  @property({ type: String })
  name = '';
  @property({ type: String })
  type = 'CABLE';
  @property({ type: String })
  connectionType = 'NONE';
  @property({ type: String })
  sector: Sector = 'OTHER';

  width = 400;
  title = t('computers.newNetworkTitle');

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'cancel' },
      { name: t('content.done'), action: 'done', main: true, disabled: this.name === '' },
    ];
  }
  _newItemNameChange(e) {
    this.name = e.detail.value;
  }

  renderBody() {
    return html`
      <d-section>
        <d-edit-text
          autofocus
          label=${t('computers.newNetworkName')}
          .value=${this.name}
          @value-changed=${(e) => this._newItemNameChange(e)}
        ></d-edit-text>
        <d-select-dropdown
          field="networks_type"
          .options=${networkTypeOptions}
          .value=${this.type}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.type = e.detail.value;
          }}
        >
        </d-select-dropdown>
        ${this.sector === 'HEALTH'
          ? html`<d-select-dropdown
              field="internetConnections_connectionType"
              .options=${getInternetConnectionTypeOptionsBySector(this.sector)}
              .value=${this.connectionType}
              @value-changed=${(e: CustomEvent<{ value: string }>) => {
                e.stopPropagation();
                this.connectionType = e.detail.value;
              }}
            >
            </d-select-dropdown>`
          : ''}
      </d-section>
    `;
  }

  protected fetchResult(detail: string | undefined): NewNetworkResult {
    if (detail === 'done') {
      return {
        name: this.name,
        type: this.type,
        connectionType: this.connectionType,
        action: 'done',
      };
    } else {
      return {
        action: 'cancel',
      };
    }
  }

  protected initializeDialog(input: NewNetworkInput) {
    this.name = input.name;
    this.type = input.type;
    this.sector = input.sector;

    // For non-HEALTH sectors, always set connectionType to 'OTHER' (outside helsenett)
    if (this.sector !== 'HEALTH') {
      this.connectionType = 'OTHER';
    } else {
      this.connectionType = input.connectionType;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'new-network-dialog': NewNetworkDialog;
  }
}
